import React, { useMemo, useCallback } from "react"; // Import necessary React hooks and components
import { useHistory } from "react-router-dom"; // Import useHistory for navigation
import demoStoremaleIcon from "../../../../assets/Images/man.png"; // Import male icon
import demoStoreallIcon from "../../../../assets/Images/teamwork.png"; // Import all staff icon
import demoStorefemaleIcon from "../../../../assets/Images/woman.png"; // Import female icon
import { getEventWithMostOverlaps } from "../../../../constants/constants"; // Import utility function to get event with most overlaps
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Import booking status enum
import { BookingTypeEnum } from "../../../../constants/enums/booking-type-enum"; // Import booking type enum
import { formatMessage } from "../../../../translations/format-message"; // Import message formatting function
import DraggableEvent from "../draggableEvent"; // Import DraggableEvent component
import AddBreakDetailsPopupHOC from "../hoc/add-break-details-popup-hoc"; // Import HOC for adding break details popup
import AllMoreEventsPopupHOC from "../hoc/all-more-events-popup-hoc"; // Import HOC for all more events popup
import ReservationDetailsPopupHOC from "../hoc/reservation-details-popup-hoc"; // Import HOC for reservation details popup
import AllMoreEventsComponent from "../modal-components/all-more-events-component"; // Import all more events modal component
import ReservationDetailsComponent from "../modal-components/reservation-details-component"; // Import reservation details modal component
import EventCSSProperties from "./event-css-properties"; // Import function for event CSS properties
import IntervalEvent from "./interval-event-component"; // Import interval event component
import ServiceNameDisplayComponent from "./services-name-component"; // Import service name display component

// Define props interface for the component
interface IProps {
  key: string; // Unique key for the component
  day: any; // Day of the event
  currentDate: any; // Current date
  dataObject: any; // Data object containing event details
  timeLabel: any; // Time label for the event
  start: any; // Start time of the event
  end: any; // End time of the event
  overlappingCount: number; // Count of overlapping events
  defaultMargin: number; // Default margin for event display
  allEvents: any[]; // Array of all events
  allBreaks: any[]; // Array of all breaks
  events: any[]; // Array of current events
  cellWidth: number; // Width of the cell in the calendar
  storeId: number; // ID of the store
  viewOnly: boolean; // Flag for view-only mode
  breaksCount?: number; // Optional count of breaks
  allMatchedBreaks?: any[]; // Optional array of matched breaks
  index?: number; // Optional index of the event
  staffList?: any[]; // Optional list of staff
  showReservationDetailsPopup?: boolean; // Flag to show reservation details popup
  reservationDetailsToggleHandler?: () => void; // Handler to toggle reservation details popup
  allMoreEventsPopup?: boolean; // Flag to show all more events popup
  allMoreEventsToggleHandler?: () => void; // Handler to toggle all more events popup
}

// Define the ReceptionEventComponent functional component
const ReceptionEventComponent: React.FC<IProps> = (props) => {
  const {
    key, // Destructure key from props
    day, // Destructure day from props
    currentDate, // Destructure currentDate from props
    dataObject, // Destructure dataObject from props
    timeLabel, // Destructure timeLabel from props
    start, // Destructure start from props
    end, // Destructure end from props
    overlappingCount, // Destructure overlappingCount from props
    defaultMargin, // Destructure defaultMargin from props
    events, // Destructure events from props
    allEvents, // Destructure allEvents from props
    allBreaks, // Destructure allBreaks from props
    cellWidth, // Destructure cellWidth from props
    viewOnly, // Destructure viewOnly from props
    breaksCount = 0, // Destructure breaksCount from props with default value
    allMatchedBreaks, // Destructure allMatchedBreaks from props
    index, // Destructure index from props
    staffList, // Destructure staffList from props
    showReservationDetailsPopup, // Destructure showReservationDetailsPopup from props
    reservationDetailsToggleHandler, // Destructure reservationDetailsToggleHandler from props
    allMoreEventsPopup, // Destructure allMoreEventsPopup from props
    allMoreEventsToggleHandler, // Destructure allMoreEventsToggleHandler from props
  } = props; // Assign destructured values to variables

  // Destructure properties from dataObject
  const {
    Id, // Event ID
    ReservationId, // Reservation ID
    CustomerName, // Customer name
    StartTime, // Start time of the event
    EndTime, // End time of the event
    AppointmentStatus, // Status of the appointment
    BookingType, // Type of booking
    ServiceRequestedList, // List of requested services
    NominationFee, // Nomination fee
    StaffPreference, // Staff preference
    IsMoreEvents, // Flag for more events
    Overlapped, // Count of overlapped events
  } = dataObject; // Assign destructured values to variables

  // Memoized values for appointment counts
  const appointmentsCounts = useMemo(() => {
    const eventWithMost = getEventWithMostOverlaps(dataObject, allEvents); // Get event with most overlaps
    return eventWithMost
      ? `${eventWithMost.OverlappedEventsIds?.length - 2}` // Return count of overlapped events
      : `${Overlapped - 2}`; // Return count of overlapped events
  }, [dataObject, allEvents, Overlapped]); // Dependencies for memoization

  const shouldShowBreaks = breaksCount > 0; // Determine if breaks should be shown

  // Handlers for opening popups
  const openReservationDetailsPopup = useCallback(() => {
    reservationDetailsToggleHandler?.(); // Call reservation details toggle handler
  }, [reservationDetailsToggleHandler]); // Dependencies for memoization

  const openAllMoreEventsPopup = useCallback(() => {
    allMoreEventsToggleHandler?.(); // Call all more events toggle handler
  }, [allMoreEventsToggleHandler]); // Dependencies for memoization

  // Calculate draggable event style
  const draggableStyle = useMemo(
    () =>
      EventCSSProperties(
        start, // Start time
        end, // End time
        overlappingCount * defaultMargin, // Calculate margin based on overlapping count
        cellWidth * (dataObject?.TotalTime / 5) // Calculate width based on total time
      ),
    [
      start,
      end,
      overlappingCount,
      defaultMargin,
      cellWidth,
      dataObject?.TotalTime,
    ] // Dependencies for memoization
  );

  // Determine staff icon based on preference
  const staffIcon = useMemo(() => {
    if (StaffPreference === 1) return demoStoremaleIcon; // Male icon for preference 1
    if (StaffPreference === 2) return demoStorefemaleIcon; // Female icon for preference 2
    return demoStoreallIcon; // All staff icon for other preferences
  }, [StaffPreference]); // Dependency for memoization

  return (
    <React.Fragment key={key}> {/* Fragment to group elements */}
      {allMoreEventsPopup && allMoreEventsToggleHandler && ( // Check if all more events popup should be shown
        <AllMoreEventsComponent
          day={day} // Pass day prop
          onModalClosed={allMoreEventsToggleHandler} // Pass modal close handler
          matchedEvent={dataObject} // Pass matched event data
          AllEvents={allEvents} // Pass all events
          staffList={staffList} // Pass staff list
          breaks={allMatchedBreaks} // Pass matched breaks
        />
      )}

      {showReservationDetailsPopup && reservationDetailsToggleHandler && ( // Check if reservation details popup should be shown
        <ReservationDetailsComponent
          onModalClosed={reservationDetailsToggleHandler} // Pass modal close handler
          event={dataObject} // Pass event data
          storeId={props.storeId} // Pass store ID
          viewOnly={viewOnly} // Pass view-only flag
        />
      )}

      <DraggableEvent
        key={Id || ReservationId || `${day}-${timeLabel}`} // Unique key for DraggableEvent
        currentDate={currentDate} // Pass current date
        area="Inner" // Define area for draggable event
        data={dataObject} // Pass event data
        style={draggableStyle} // Pass calculated style
      >
        {({ onResizingChange }) => ( // Render function for DraggableEvent
          <>
            <div className="event reception">
              <div onClick={openReservationDetailsPopup} style={{ cursor: "pointer" }}>
                <div>{CustomerName}</div>
                <div>{StartTime} - {EndTime}</div>
                 {/* ID: {props.dataObject?.Id} , OId: {props.dataObject?.OverlappedEventsIds?.join(", ")} */}
                <div className="reception-event-details">
                  {AppointmentStatus === bookingStatusEnum.InProcess && (
                    <span className="badge bg-warning">{formatMessage("status_InProcess")}</span>
                  )}

                  {BookingType === BookingTypeEnum.NewWeb && (
                    <span className="event-mode bg-danger position-relative">
                      <p className="text-white">New</p>
                    </span>
                  )}

                  <span className="event-mode">
                    {BookingType === BookingTypeEnum.Web || BookingType === BookingTypeEnum.NewWeb ? (
                      <i className="bi bi-globe"></i>
                    ) : BookingType === BookingTypeEnum.Visit || BookingType === BookingTypeEnum.NewVisit ? (
                      <i className="bi bi-shop-window"></i>
                    ) : null}
                  </span>
                </div>

                <div className="treatment-name">
                  <ServiceNameDisplayComponent services={ServiceRequestedList} />
                </div>

                <div className="supporting-details my-2 d-flex align-items-center gap-2">
                  {StaffPreference == 3 && <span className="event-mode nomination-fee">指</span>}
                  {dataObject?.StaffPreference == 1 && (
                  <span className="event-mode staff-preference">
                    <img src={demoStoremaleIcon} />
                  </span>
                  )}
                  {dataObject?.StaffPreference == 2 && (
                  <span className="event-mode staff-preference">
                    <img src={demoStorefemaleIcon} />
                  </span>
                  )}
                </div>
              </div>

              <div className="row">
                {(IsMoreEvents || (events?.length <= 2 && shouldShowBreaks && index === 1)) && (
                  <div className="col-lg-4 more-events" style={{ cursor: "pointer" }} onClick={openAllMoreEventsPopup}>
                    + {appointmentsCounts !== "0" && `${appointmentsCounts} ${formatMessage("more")}`}
                    {shouldShowBreaks && (
                      <span>
                        {appointmentsCounts !== "0" && ", "} {breaksCount} {breaksCount > 1 ? formatMessage("breaks") : formatMessage("break")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <IntervalEvent
              day={day} // Pass day prop
              style={cellWidth} // Pass cell width for styling
              onResizingChange={onResizingChange} // Pass resizing change handler
              dataObject={dataObject} // Pass event data
              allEvents={allEvents} // Pass all events
              allBreaks={allBreaks} // Pass all breaks
              disabled={viewOnly} // Pass view-only flag
            />
          </>
        )}
      </DraggableEvent>
    </React.Fragment>
  );
};

// Export the component wrapped with HOCs for additional functionality
export default AddBreakDetailsPopupHOC(
  AllMoreEventsPopupHOC(ReservationDetailsPopupHOC(ReceptionEventComponent))
);

